// The opening text of a chat's code, CSV and report cards, fetched together
// (#2022). Each card used to download its whole file to show a few lines, so
// a chat with many outputs sent one request per output on open (Sentry's
// N+1, PLATINUM-FRONTEND-D). Cards that mount in the same tick now share one
// POST /agent/session/:id/artifacts/excerpts. An id the server leaves out
// (not stored yet, lost, or no access) resolves to null, and the card falls
// back to its own download.

export interface ArtifactExcerptRequest {
  apiBaseUrl: string
  authHeader?: string
  sessionId: string
  artifactId: string
}

interface Waiter {
  resolve: (text: string | null) => void
  reject: (err: unknown) => void
}

interface Batch {
  apiBaseUrl: string
  authHeader?: string
  sessionId: string
  waiters: Map<string, Waiter[]>
}

/** The server's cap per request (maxArtifactExcerptIDs). */
const MAX_IDS = 100

const pending = new Map<string, Batch>()

/** The excerpt for one artifact, or null when the card must fetch it itself. */
export function requestArtifactExcerpt(req: ArtifactExcerptRequest): Promise<string | null> {
  const key = `${req.apiBaseUrl}\n${req.sessionId}\n${req.authHeader ?? ''}`
  let batch = pending.get(key)
  if (!batch) {
    batch = { apiBaseUrl: req.apiBaseUrl, authHeader: req.authHeader, sessionId: req.sessionId, waiters: new Map() }
    pending.set(key, batch)
    const scheduled = batch
    setTimeout(() => {
      pending.delete(key)
      void flush(scheduled)
    }, 0)
  }
  const current = batch
  return new Promise((resolve, reject) => {
    const list = current.waiters.get(req.artifactId) ?? []
    list.push({ resolve, reject })
    current.waiters.set(req.artifactId, list)
  })
}

async function flush(batch: Batch): Promise<void> {
  const ids = [...batch.waiters.keys()]
  for (let i = 0; i < ids.length; i += MAX_IDS) {
    const chunk = ids.slice(i, i + MAX_IDS)
    try {
      const excerpts = await fetchExcerpts(batch, chunk)
      for (const id of chunk) {
        const text = excerpts[id]?.text ?? null
        for (const w of batch.waiters.get(id) ?? []) w.resolve(text)
      }
    } catch (err) {
      for (const id of chunk) {
        for (const w of batch.waiters.get(id) ?? []) w.reject(err)
      }
    }
  }
}

async function fetchExcerpts(batch: Batch, ids: string[]): Promise<Record<string, { text: string }>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (batch.authHeader) headers['Authorization'] = batch.authHeader
  const res = await fetch(`${batch.apiBaseUrl}/agent/session/${encodeURIComponent(batch.sessionId)}/artifacts/excerpts`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ids }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json() as { excerpts?: Record<string, { text: string }> }
  return data.excerpts ?? {}
}
